import { FC, ReactElement, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Collapse,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Delete,
  Add,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useGetValidationCheckDefinitionsQuery,
  useToggleValidationCheckMutation,
  useToggleValidationCheckItemMutation,
  useDeleteValidationCheckMutation,
  useCreateValidationCheckMutation,
  useUpdateValidationCheckMutation,
} from "@/graphql/validation-check-definitions";

const KNOWN_SLOTS = [
  "middleman_po",
  "factory_po",
  "middleman_inv",
  "factory_inv",
  "middleman_payment",
  "factory_payment",
  "affidavit",
  "packing_list",
  "bill_of_lading",
  "cost_breakdown_sheet",
  "assist_invoice",
  "container_photos",
  "checklist1",
  "checklist2",
];

// ─── Check Card ─────────────────────────────────────────────────────

interface CheckCardProps {
  check: TValidationCheckDefinition;
  onToggle: (checkId: string, enabled: boolean) => void;
  onToggleItem: (checkId: string, itemId: string, enabled: boolean) => void;
  onDelete: (checkId: string) => void;
  onEdit: (check: TValidationCheckDefinition) => void;
}

const CheckCard: FC<CheckCardProps> = ({
  check,
  onToggle,
  onToggleItem,
  onDelete,
  onEdit,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.border.light}`,
        borderRadius: tokens.radius.md,
        mb: 1,
        opacity: check.enabled ? 1 : 0.6,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
          <Typography
            sx={{
              fontWeight: tokens.fontWeight.semibold,
              fontSize: tokens.fontSize.sm2,
              color: tokens.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {check.checkName}
          </Typography>
          {check.isCustom && (
            <Chip label="Custom" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
          )}
          {check.checkType === "cross_doc" && (
            <Chip label="Cross-Doc" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            size="small"
            onClick={() => onEdit(check)}
            sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, minWidth: "auto", px: 1 }}
          >
            Edit
          </Button>
          {check.isCustom && (
            <IconButton size="small" color="error" onClick={() => onDelete(check.checkId)}>
              <Delete fontSize="small" />
            </IconButton>
          )}
          <Switch
            size="small"
            checked={check.enabled}
            onChange={(_, val) => onToggle(check.checkId, val)}
          />
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 1.5, borderTop: `1px solid ${tokens.border.light}` }}>
          {check.description && (
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 1, mb: 0.5 }}>
              {check.description}
            </Typography>
          )}
          {check.targetSlotId && (
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              Target: <strong>{check.targetSlotId}</strong>
            </Typography>
          )}
          {check.requiredSlots && check.requiredSlots.length > 0 && (
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              Required slots: <strong>{check.requiredSlots.join(", ")}</strong>
            </Typography>
          )}
          {check.condition && (
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              Condition: <strong>{check.condition}</strong>
            </Typography>
          )}

          {check.items && check.items.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, mb: 0.5 }}>
                Items ({check.items.filter((i) => i.enabled).length}/{check.items.length} enabled)
              </Typography>
              {check.items.map((item) => (
                <Stack
                  key={item.itemId}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    py: 0.25,
                    opacity: item.enabled ? 1 : 0.5,
                  }}
                >
                  <Switch
                    size="small"
                    checked={item.enabled}
                    onChange={(_, val) => onToggleItem(check.checkId, item.itemId, val)}
                  />
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>
                    <strong>{item.itemId}</strong> — {item.question}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}

          <Box sx={{ mt: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                color: tokens.text.disabled,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                maxHeight: 120,
                overflow: "auto",
                bgcolor: tokens.bg.muted,
                p: 1,
                borderRadius: tokens.radius.sm,
              }}
            >
              {check.promptTemplate.slice(0, 500)}
              {check.promptTemplate.length > 500 ? "..." : ""}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Add/Edit Dialog ────────────────────────────────────────────────

interface CheckDialogProps {
  open: boolean;
  onClose: () => void;
  editCheck?: TValidationCheckDefinition | null;
}

const CheckDialog: FC<CheckDialogProps> = ({ open, onClose, editCheck }) => {
  const isEdit = !!editCheck;
  const [createCheck] = useCreateValidationCheckMutation();
  const [updateCheck] = useUpdateValidationCheckMutation();

  const [checkId, setCheckId] = useState("");
  const [checkName, setCheckName] = useState("");
  const [checkType, setCheckType] = useState<"document_checklist" | "cross_doc">("document_checklist");
  const [targetSlotId, setTargetSlotId] = useState("");
  const [description, setDescription] = useState("");
  const [requiredSlots, setRequiredSlots] = useState<string[]>([]);
  const [promptTemplate, setPromptTemplate] = useState("");
  const [items, setItems] = useState<{ itemId: string; question: string; enabled: boolean }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset form when dialog opens
  useMemo(() => {
    if (open && editCheck) {
      setCheckId(editCheck.checkId);
      setCheckName(editCheck.checkName);
      setCheckType(editCheck.checkType);
      setTargetSlotId(editCheck.targetSlotId || "");
      setDescription(editCheck.description || "");
      setRequiredSlots(editCheck.requiredSlots || []);
      setPromptTemplate(editCheck.promptTemplate);
      setItems(
        (editCheck.items || []).map((i) => ({
          itemId: i.itemId,
          question: i.question,
          enabled: i.enabled,
        }))
      );
    } else if (open) {
      setCheckId("");
      setCheckName("");
      setCheckType("document_checklist");
      setTargetSlotId("");
      setDescription("");
      setRequiredSlots([]);
      setPromptTemplate("");
      setItems([]);
    }
    setError("");
  }, [open, editCheck]);

  const handleAddItem = () => {
    setItems([...items, { itemId: "", question: "", enabled: true }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: "itemId" | "question", value: string) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const handleSave = async () => {
    setError("");
    if (!checkId.trim() || !checkName.trim() || !promptTemplate.trim()) {
      setError("Check ID, name, and prompt template are required.");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateCheck({
          checkId,
          input: {
            checkName,
            targetSlotId: checkType === "document_checklist" ? targetSlotId : undefined,
            description: checkType === "cross_doc" ? description : undefined,
            requiredSlots: checkType === "cross_doc" ? requiredSlots : undefined,
            promptTemplate,
            items: checkType === "document_checklist" ? items.map((i) => ({ ...i, crossRefSlots: [] })) : undefined,
          },
        }).unwrap();
      } else {
        await createCheck({
          input: {
            checkId,
            checkName,
            checkType,
            targetSlotId: checkType === "document_checklist" ? targetSlotId : undefined,
            description: checkType === "cross_doc" ? description : undefined,
            requiredSlots: checkType === "cross_doc" ? requiredSlots : undefined,
            promptTemplate,
            items: checkType === "document_checklist" ? items.map((i) => ({ ...i, crossRefSlots: [] })) : undefined,
          },
        }).unwrap();
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save check");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Check" : "Add Custom Check"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Check ID"
              value={checkId}
              onChange={(e) => setCheckId(e.target.value)}
              disabled={isEdit}
              size="small"
              fullWidth
            />
            <TextField
              label="Check Name"
              value={checkName}
              onChange={(e) => setCheckName(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>

          {!isEdit && (
            <FormControl size="small">
              <InputLabel>Check Type</InputLabel>
              <Select
                value={checkType}
                label="Check Type"
                onChange={(e) => setCheckType(e.target.value as "document_checklist" | "cross_doc")}
              >
                <MenuItem value="document_checklist">Document Checklist</MenuItem>
                <MenuItem value="cross_doc">Cross-Document</MenuItem>
              </Select>
            </FormControl>
          )}

          {checkType === "document_checklist" && (
            <>
              <FormControl size="small">
                <InputLabel>Target Slot</InputLabel>
                <Select
                  value={targetSlotId}
                  label="Target Slot"
                  onChange={(e) => setTargetSlotId(e.target.value)}
                >
                  {KNOWN_SLOTS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm2 }}>
                    Checklist Items
                  </Typography>
                  <Button size="small" startIcon={<Add />} onClick={handleAddItem} sx={{ textTransform: "none" }}>
                    Add Item
                  </Button>
                </Stack>
                {items.map((item, idx) => (
                  <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      label="Item ID"
                      value={item.itemId}
                      onChange={(e) => handleItemChange(idx, "itemId", e.target.value)}
                      sx={{ width: 200 }}
                    />
                    <TextField
                      size="small"
                      label="Question"
                      value={item.question}
                      onChange={(e) => handleItemChange(idx, "question", e.target.value)}
                      fullWidth
                    />
                    <IconButton size="small" color="error" onClick={() => handleRemoveItem(idx)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Box>
            </>
          )}

          {checkType === "cross_doc" && (
            <>
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                fullWidth
              />
              <FormControl size="small">
                <InputLabel>Required Slots</InputLabel>
                <Select
                  multiple
                  value={requiredSlots}
                  label="Required Slots"
                  onChange={(e) => setRequiredSlots(e.target.value as string[])}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {KNOWN_SLOTS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <TextField
            label="Prompt Template"
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            multiline
            minRows={6}
            maxRows={15}
            size="small"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {saving ? <CircularProgress size={20} /> : isEdit ? "Save Changes" : "Create Check"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────

const ValidationChecksSettings: FC = (): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const isTDAdmin = user?.userRole === roles.TDADMIN;

  const { data: checks, isLoading } = useGetValidationCheckDefinitionsQuery();
  const [toggleCheck] = useToggleValidationCheckMutation();
  const [toggleItem] = useToggleValidationCheckItemMutation();
  const [deleteCheck] = useDeleteValidationCheckMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState<TValidationCheckDefinition | null>(null);

  const docChecks = useMemo(
    () => (checks || []).filter((c) => c.checkType === "document_checklist"),
    [checks]
  );
  const crossDocChecks = useMemo(
    () => (checks || []).filter((c) => c.checkType === "cross_doc"),
    [checks]
  );

  const handleToggle = useCallback(
    (checkId: string, enabled: boolean) => {
      toggleCheck({ checkId, enabled });
    },
    [toggleCheck]
  );

  const handleToggleItem = useCallback(
    (checkId: string, itemId: string, enabled: boolean) => {
      toggleItem({ checkId, itemId, enabled });
    },
    [toggleItem]
  );

  const handleDelete = useCallback(
    (checkId: string) => {
      if (window.confirm("Delete this custom check? This cannot be undone.")) {
        deleteCheck({ checkId });
      }
    },
    [deleteCheck]
  );

  const handleEdit = useCallback((check: TValidationCheckDefinition) => {
    setEditingCheck(check);
    setDialogOpen(true);
  }, []);

  const handleAdd = useCallback(() => {
    setEditingCheck(null);
    setDialogOpen(true);
  }, []);

  if (!isTDAdmin) {
    return (
      <Box px={2.5} py={3}>
        <Typography sx={{ color: tokens.text.muted }}>
          You do not have permission to view this page.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box px={2.5} pt={2} pb={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                fontWeight: tokens.fontWeight.bold,
                color: tokens.text.primary,
                fontSize: tokens.fontSize.xl,
              }}
            >
              Validation Checks
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.muted, mt: 0.25 }}>
              Manage First Sale validation checklists and cross-document checks
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Add Check
          </Button>
        </Stack>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, px: 2.5, py: 1.5 }}>
            <Typography
              sx={{
                fontWeight: tokens.fontWeight.semibold,
                fontSize: tokens.fontSize.md,
                color: tokens.text.primary,
                mb: 1,
              }}
            >
              Document Checklists ({docChecks.length})
            </Typography>
            {docChecks.map((check) => (
              <CheckCard
                key={check.checkId}
                check={check}
                onToggle={handleToggle}
                onToggleItem={handleToggleItem}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            ))}
          </Box>

          <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, px: 2.5, py: 1.5 }}>
            <Typography
              sx={{
                fontWeight: tokens.fontWeight.semibold,
                fontSize: tokens.fontSize.md,
                color: tokens.text.primary,
                mb: 1,
              }}
            >
              Cross-Document Checks ({crossDocChecks.length})
            </Typography>
            {crossDocChecks.map((check) => (
              <CheckCard
                key={check.checkId}
                check={check}
                onToggle={handleToggle}
                onToggleItem={handleToggleItem}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            ))}
          </Box>
        </>
      )}

      <CheckDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editCheck={editingCheck}
      />
    </>
  );
};

export default ValidationChecksSettings;
